import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { RewardChip } from '@/features/guide-rewards/components/RewardChip';
import { adminGrowthApi, adminGrowthKeys, type AdminReward } from '../api/admin-growth.api';
import { FilterBar, GrowthTable, ReasonDialog, fmtDate, fmtDateTime, fmtNum, humanize, useGrowthMutation } from './GrowthShared';

type Action = 'approve' | 'reject' | 'reverse';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 1, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Box sx={{ fontSize: '0.875rem', minWidth: 0, wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

function RewardDetailDrawer({ id, onClose, onAction }: { id: string | null; onClose: () => void; onAction: (action: Action, reward: AdminReward) => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: adminGrowthKeys.reward(id ?? ''),
    queryFn: () => adminGrowthApi.reward(id!),
    enabled: Boolean(id),
  });
  const reward = data?.reward;

  return (
    <Drawer anchor="right" open={Boolean(id)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>Reward details</Typography>
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2.5, display: 'grid', gap: 2.5, overflowY: 'auto' }}>
        {isLoading && <Skeleton variant="rounded" height={240} />}
        {isError && <Alert severity="error">Could not load this reward.</Alert>}
        {reward && data && (
          <>
            <Box>
              <Typography fontWeight={800} fontSize="1.1rem">{reward.snapshot.activityTitle}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <RewardChip label={humanize(reward.status)} status={reward.status} />
                <Typography fontWeight={800}>{fmtNum(reward.credits)} credits</Typography>
                {reward.needsReview && <RewardChip label="Evidence changed" tone="error" />}
              </Box>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Guide</Typography>
              <Row label="Name">{data.guide?.name ?? '—'}</Row>
              <Row label="Guide ID">{data.guide?.guideId ?? '—'}</Row>
              <Row label="Email">{data.guide?.email ?? '—'}</Row>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Eligibility evidence</Typography>
              <Row label="Rule (snapshot)">
                {reward.snapshot.trigger === 'MILESTONE'
                  ? `${reward.snapshot.criteria.bookings ?? 0} bookings · ${reward.snapshot.criteria.travellerPhotos ?? 0} photos`
                  : humanize(reward.snapshot.trigger)}
                {' · '}{humanize(reward.snapshot.approvalMode)} approval
              </Row>
              {reward.snapshot.campaignName && <Row label="Campaign">{reward.snapshot.campaignName}</Row>}
              {Object.entries(reward.snapshot.evidence ?? {}).map(([key, value]) => (
                <Row key={key} label={humanize(key.replace(/([A-Z])/g, '_$1'))}>
                  {Array.isArray(value) ? `${value.length}: ${value.join(', ')}` : String(value ?? '—')}
                </Row>
              ))}
              <Row label="Created">{fmtDateTime(reward.createdAt)}</Row>
            </Box>

            {data.referral && (
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>Referral</Typography>
                <Row label="Referred guide">{data.referral.referred.name} {data.referral.referred.guideId ? `(${data.referral.referred.guideId})` : ''}</Row>
                <Row label="Status">{humanize(data.referral.status)}</Row>
                <Row label="Source">{data.referral.source === 'LINK' ? 'Referral link' : 'Typed Guide ID'} · {data.referral.code}</Row>
                <Row label="Applied">{fmtDate(data.referral.createdAt)}</Row>
                <Row label="Approved">{fmtDate(data.referral.approvedAt)}</Row>
              </Box>
            )}

            {data.transactions.length > 0 && (
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>Ledger</Typography>
                {data.transactions.map((tx) => (
                  <Row key={tx._id} label={humanize(tx.type)}>{tx.amount > 0 ? '+' : ''}{fmtNum(tx.amount)} · {fmtDateTime(tx.createdAt)}</Row>
                ))}
              </Box>
            )}

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Admin history</Typography>
              {data.history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No admin actions yet.</Typography>
              ) : (
                data.history.map((h) => (
                  <Box key={h._id} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight={600}>{humanize(h.action)} · {h.actorName ?? 'Admin'}</Typography>
                    <Typography variant="caption" color="text.secondary">{fmtDateTime(h.createdAt)}{h.reason ? ` — ${h.reason}` : ''}</Typography>
                  </Box>
                ))
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {reward.status === 'PENDING_APPROVAL' && (
                <>
                  <Button variant="contained" color="success" onClick={() => onAction('approve', reward)}>Approve & credit</Button>
                  <Button variant="outlined" color="error" onClick={() => onAction('reject', reward)}>Reject</Button>
                </>
              )}
              {reward.status === 'CREDITED' && (
                <Button variant="outlined" color="error" onClick={() => onAction('reverse', reward)}>Reverse credits</Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}

export function GrowthApprovalsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('id');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState('PENDING_APPROVAL');
  const [search, setSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [pending, setPending] = useState<{ action: Action; reward: AdminReward } | null>(null);

  const query = { page: page + 1, limit, status: status || undefined, search: search.trim() || undefined, needsReview: flaggedOnly || undefined };
  const { data, isLoading, isError } = useQuery({
    queryKey: adminGrowthKeys.rewards(query),
    queryFn: () => adminGrowthApi.rewards(query),
    placeholderData: keepPreviousData,
  });

  const setOpenId = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('id', id);
    else next.delete('id');
    setSearchParams(next, { replace: true });
  };

  const approve = useGrowthMutation(({ id, note }: { id: string; note?: string }) => adminGrowthApi.approveReward(id, note), 'Reward approved and credited');
  const reject = useGrowthMutation(({ id, reason }: { id: string; reason: string }) => adminGrowthApi.rejectReward(id, reason), 'Reward rejected');
  const reverse = useGrowthMutation(({ id, reason }: { id: string; reason: string }) => adminGrowthApi.reverseReward(id, reason), 'Reward reversed');
  const busy = approve.isPending || reject.isPending || reverse.isPending;

  const confirm = (reason: string) => {
    if (!pending) return;
    const id = pending.reward._id;
    const done = { onSuccess: () => setPending(null) };
    if (pending.action === 'approve') approve.mutate({ id, note: reason || undefined }, done);
    if (pending.action === 'reject') reject.mutate({ id, reason }, done);
    if (pending.action === 'reverse') reverse.mutate({ id, reason }, done);
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Approving credits the guide's wallet once — approving again is a no-op. Credited rewards can only be corrected with a reversal, which keeps the original ledger entry.
      </Alert>
      <FilterBar>
        <TextField size="small" select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {['PENDING_APPROVAL', 'CREDITED', 'REJECTED', 'REVERSED'].map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Search guide" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        <FormControlLabel control={<Switch checked={flaggedOnly} onChange={(e) => { setFlaggedOnly(e.target.checked); setPage(0); }} />} label="Flagged only" />
      </FilterBar>
      <GrowthTable
        loading={isLoading}
        error={isError}
        rows={data?.items}
        rowKey={(r) => r._id}
        onRowClick={(r) => setOpenId(r._id)}
        emptyText="Nothing here — no rewards match these filters."
        page={page}
        limit={limit}
        total={data?.total}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(0); }}
        columns={[
          { key: 'guide', label: 'Guide', render: (r) => <><Typography fontWeight={600} fontSize="0.875rem">{r.guide?.name ?? '—'}</Typography><Typography variant="caption" color="text.secondary">{r.guide?.guideId ?? ''}</Typography></> },
          { key: 'activity', label: 'Activity', render: (r) => <>{r.snapshot.activityTitle}{r.needsReview && <FlagOutlinedIcon color="error" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} titleAccess="Evidence changed" />}</> },
          { key: 'credits', label: 'Credits', align: 'right', render: (r) => <b>{fmtNum(r.credits)}</b> },
          { key: 'status', label: 'Status', render: (r) => <RewardChip label={humanize(r.status)} status={r.status} /> },
          { key: 'created', label: 'Created', render: (r) => fmtDate(r.createdAt) },
          {
            key: 'actions',
            label: '',
            align: 'right',
            render: (r) =>
              r.status === 'PENDING_APPROVAL' ? (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                  <Button size="small" variant="contained" color="success" onClick={() => setPending({ action: 'approve', reward: r })}>Approve</Button>
                  <Button size="small" color="error" onClick={() => setPending({ action: 'reject', reward: r })}>Reject</Button>
                </Box>
              ) : null,
          },
        ]}
      />
      <RewardDetailDrawer id={openId} onClose={() => setOpenId(null)} onAction={(action, reward) => setPending({ action, reward })} />
      <ReasonDialog
        open={Boolean(pending)}
        pending={busy}
        required={pending?.action !== 'approve'}
        danger={pending?.action !== 'approve'}
        title={pending?.action === 'approve' ? 'Approve and credit this reward?' : pending?.action === 'reject' ? 'Reject this reward?' : 'Reverse these credits?'}
        description={pending ? `${pending.reward.snapshot.activityTitle} · ${fmtNum(pending.reward.credits)} credits for ${pending.reward.guide?.name ?? 'this guide'}.` : undefined}
        confirmLabel={pending?.action === 'approve' ? 'Approve & credit' : pending?.action === 'reject' ? 'Reject' : 'Reverse'}
        onClose={() => setPending(null)}
        onConfirm={confirm}
      />
    </>
  );
}
